import { AnimeCatJsonRoot } from '../types';
import { buildApiUrl } from './apiConfig';

/**
 * Normalizes common remote URLs (e.g. converting GitHub web links to raw links,
 * Google Drive sharing links to direct download, Pastebin to raw, etc.)
 */
export function normalizeRemoteUrl(url: string): string {
  let clean = url.trim();

  // GitHub Repo Blob / Raw link
  if (clean.includes('github.com') && clean.includes('/blob/')) {
    clean = clean.replace('github.com', 'raw.githubusercontent.com').replace('/blob/', '/');
  } else if (clean.includes('github.com') && clean.includes('/raw/')) {
    clean = clean.replace('github.com', 'raw.githubusercontent.com').replace('/raw/', '/');
  }

  // GitHub Gist link
  if (clean.includes('gist.github.com/') && !clean.includes('gist.githubusercontent.com') && !clean.endsWith('/raw')) {
    clean = clean.replace('gist.github.com/', 'gist.githubusercontent.com/') + '/raw';
  }

  // GitLab link
  if (clean.includes('gitlab.com') && clean.includes('/-/blob/')) {
    clean = clean.replace('/-/blob/', '/-/raw/');
  }

  // Bitbucket link
  if (clean.includes('bitbucket.org') && clean.includes('/src/')) {
    clean = clean.replace('/src/', '/raw/');
  }

  // Pastebin link
  if (clean.includes('pastebin.com/') && !clean.includes('pastebin.com/raw/')) {
    clean = clean.replace('pastebin.com/', 'pastebin.com/raw/');
  }

  // Rentry link
  if (clean.includes('rentry.co/') && !clean.endsWith('/raw')) {
    clean = clean.replace(/\/?$/, '/raw');
  }

  // Paste.ee link
  if (clean.includes('paste.ee/p/')) {
    clean = clean.replace('paste.ee/p/', 'paste.ee/r/');
  }

  // Dropbox link
  if (clean.includes('dropbox.com/')) {
    clean = clean.replace('dl=0', 'raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    if (!clean.includes('raw=1') && !clean.includes('dl=1')) {
      clean += (clean.includes('?') ? '&' : '?') + 'raw=1';
    }
  }

  // Google Drive share link
  const gDriveMatch = clean.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
  if (gDriveMatch && gDriveMatch[1]) {
    clean = `https://drive.usercontent.google.com/download?id=${gDriveMatch[1]}&export=download&confirm=t`;
  }

  return clean;
}

/**
 * Sanitizes and parses loosely formatted JSON text (handles BOM, comments, trailing commas)
 */
export function sanitizeAndParseJson(rawText: string): any {
  const cleanText = rawText.replace(/^\uFEFF/, '').trim();
  try {
    return JSON.parse(cleanText);
  } catch {
    const sanitized = cleanText
      .replace(/\/\*[\s\S]*?\*\/|([^:]|^)\/\/.*$/gm, '$1') // strip comments
      .replace(/,\s*([}\]])/g, '$1'); // strip trailing commas
    return JSON.parse(sanitized);
  }
}

/**
 * Fetches JSON from a remote URL with multi-layer fallback strategy:
 * 1. Backend proxy endpoint /api/fetch-json (Bypasses CORS entirely)
 * 2. Direct client fetch with permissive headers
 * 3. Public CORS proxies (AllOrigins, CorsProxy, CodeTabs)
 */
export async function fetchRemoteJson(url: string): Promise<{ data: any; rawText: string }> {
  const normalizedUrl = normalizeRemoteUrl(url);
  if (!normalizedUrl || !normalizedUrl.startsWith('http')) {
    throw new Error('La URL ha de començar per http:// o https://');
  }

  // Strategy 0: Native Android Bridge (Android TV, Google TV, Mobile WebAppInterface)
  // Bypasses browser CORS and WebView origin restrictions completely
  if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.fetchUrl === 'function') {
    try {
      const text = (window as any).Android.fetchUrl(normalizedUrl);
      if (text && text.trim().length > 0) {
        const data = sanitizeAndParseJson(text);
        if (data && typeof data === 'object') {
          return { data, rawText: JSON.stringify(data, null, 2) };
        }
      }
    } catch (androidErr) {
      console.warn("Android native fetchUrl failed, trying web fallbacks:", androidErr);
    }
  }

  let serverErrorMessage = '';

  // Strategy 1: Server-Side Backend Proxy (Robust against CORS, User-Agent, and redirects)
  try {
    const proxyUrl = buildApiUrl(`/api/fetch-json?url=${encodeURIComponent(normalizedUrl)}`);
    const res = await fetch(proxyUrl, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    });

    if (res.ok) {
      const data = await res.json();
      if (data !== undefined && data !== null) {
        return { data, rawText: JSON.stringify(data, null, 2) };
      }
    } else {
      const errorJson = await res.json().catch(() => null);
      if (errorJson?.error) {
        serverErrorMessage = errorJson.error;
      }
    }
  } catch (err: any) {
    serverErrorMessage = err.message || 'Error de connexió amb el servidor intermediari';
  }

  // Strategy 2: Direct Client Fetch
  try {
    const res = await fetch(normalizedUrl, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      }
    });
    if (res.ok) {
      const text = await res.text();
      const data = sanitizeAndParseJson(text);
      return { data, rawText: typeof data === 'object' ? JSON.stringify(data, null, 2) : text };
    }
  } catch {
    // ignore CORS error and try proxies
  }

  // Strategy 3: CORS proxy AllOrigins
  try {
    const fallbackUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(normalizedUrl)}`;
    const res = await fetch(fallbackUrl);
    if (res.ok) {
      const text = await res.text();
      const data = sanitizeAndParseJson(text);
      return { data, rawText: typeof data === 'object' ? JSON.stringify(data, null, 2) : text };
    }
  } catch {
    // ignore and continue
  }

  // Strategy 4: CORS proxy CorsProxy.io
  try {
    const fallbackUrl = `https://corsproxy.io/?url=${encodeURIComponent(normalizedUrl)}`;
    const res = await fetch(fallbackUrl);
    if (res.ok) {
      const text = await res.text();
      const data = sanitizeAndParseJson(text);
      return { data, rawText: typeof data === 'object' ? JSON.stringify(data, null, 2) : text };
    }
  } catch {
    // ignore and continue
  }

  // Strategy 5: CORS proxy CodeTabs
  try {
    const fallbackUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(normalizedUrl)}`;
    const res = await fetch(fallbackUrl);
    if (res.ok) {
      const text = await res.text();
      const data = sanitizeAndParseJson(text);
      return { data, rawText: typeof data === 'object' ? JSON.stringify(data, null, 2) : text };
    }
  } catch {
    // ignore
  }

  const errorMessage = serverErrorMessage || 'No s\'ha pogut obtenir el fitxer JSON degut a restriccions de xarxa o de CORS del servidor remot.';
  throw new Error(errorMessage);
}
